import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MessageSquare,
  Bot,
  User,
  Send,
  Sparkles,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';

// ---- Types ----

interface ConversationSummary {
  id: string;
  channel: string;
  contactName: string | null;
  contactPhone: string | null;
  status: string;
  aiTakeoverActive: boolean;
  sentimentTrend: number | null;
  updatedAt: string;
}

interface MessageDto {
  id: string;
  conversationId: string;
  direction: string;
  source: string;
  body: string;
  sentBy: string | null;
  sentiment: number | null;
  createdAt: string;
}

interface ConversationDetail {
  id: string;
  channel: string;
  contactName: string | null;
  contactPhone: string | null;
  status: string;
  aiTakeoverActive: boolean;
  aiTakeoverAgentId: string | null;
  sentimentTrend: number | null;
  messages: MessageDto[];
}

interface SparklinePoint {
  position: number;
  score: number;
}

interface SentimentSparkline {
  conversationId: string;
  points: SparklinePoint[];
}

// ---- Helpers ----

function sentimentIcon(score: number | null) {
  if (score === null) return <Minus className="h-3.5 w-3.5 text-muted-foreground" />;
  if (score > 0.2) return <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />;
  if (score < -0.2) return <TrendingDown className="h-3.5 w-3.5 text-red-500" />;
  return <Minus className="h-3.5 w-3.5 text-amber-500" />;
}

function formatTime(isoStr: string) {
  const d = new Date(isoStr);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const CHANNEL_COLORS: Record<string, string> = {
  SMS: 'bg-blue-900/40 text-blue-200',
  WHATSAPP: 'bg-emerald-900/40 text-emerald-200',
  EMAIL: 'bg-violet-100 text-violet-800',
  WEBCHAT: 'bg-amber-900/40 text-amber-200',
};

// ---- SentimentSparklineSvg ----

function SentimentSparklineSvg({ points }: { points: SparklinePoint[] }) {
  if (points.length < 2) return null;
  const W = 64;
  const H = 24;
  const coords = points.map((p) => ({
    x: p.position * W,
    y: H / 2 - (p.score * H) / 2,
  }));
  return (
    <svg width={W} height={H} className="overflow-visible">
      <polyline
        points={coords.map((c) => `${c.x},${c.y}`).join(' ')}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        className="text-indigo-500"
      />
      {coords.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r={2} className="fill-indigo-500" />
      ))}
      <line x1="0" y1={H / 2} x2={W} y2={H / 2} stroke="#e5e7eb" strokeWidth="0.5" />
    </svg>
  );
}

// ---- Main Component ----

export function ConversationsPage() {
  const { t } = useTranslation();
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [selected, setSelected] = useState<ConversationDetail | null>(null);
  const [sparkline, setSparkline] = useState<SparklinePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'OPEN' | 'RESOLVED'>('all');
  const [messageBody, setMessageBody] = useState('');
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const { data } = await apiClient.get<ConversationSummary[]>(
        `/inbox/conversations${params}`,
      );
      setConversations(data);
    } catch {
      showToast(t('conversations.error_load'));
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [filter, t]);

  useEffect(() => {
    load();
  }, [load]);

  async function selectConversation(conv: ConversationSummary) {
    try {
      const [detailRes, sparkRes] = await Promise.all([
        apiClient.get<ConversationDetail>(`/inbox/conversations/${conv.id}`),
        apiClient.get<SentimentSparkline>(`/sentiment/conversations/${conv.id}/sparkline`).catch(
          () => ({ data: { conversationId: conv.id, points: [] } }),
        ),
      ]);
      setSelected(detailRes.data);
      setSparkline(sparkRes.data.points);
      setDraft(null);
    } catch {
      showToast(t('conversations.error_load'));
    }
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selected?.messages]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  async function handleSend() {
    if (!selected || !messageBody.trim()) return;
    setSending(true);
    try {
      await apiClient.post(`/inbox/conversations/${selected.id}/reply`, {
        body: messageBody.trim(),
      });
      setMessageBody('');
      setDraft(null);
      // Refresh conversation
      await selectConversation({ ...selected, updatedAt: new Date().toISOString() });
      showToast(t('conversations.sent'));
    } catch {
      showToast(t('conversations.error_send'));
    } finally {
      setSending(false);
    }
  }

  async function handleSuggest() {
    if (!selected) return;
    setSuggesting(true);
    setDraft(null);
    try {
      const { data } = await apiClient.post<{ draft: string }>(
        `/inbox/conversations/${selected.id}/suggest-reply`,
      );
      setDraft(data.draft);
    } catch {
      showToast(t('conversations.error_suggest'));
    } finally {
      setSuggesting(false);
    }
  }

  function useDraft() {
    if (draft) {
      setMessageBody(draft);
      setDraft(null);
    }
  }

  async function toggleAiTakeover() {
    if (!selected) return;
    try {
      const next = !selected.aiTakeoverActive;
      const { data } = await apiClient.patch<ConversationSummary>(
        `/inbox/conversations/${selected.id}/ai-takeover`,
        { active: next, agentId: null },
      );
      setSelected((prev) => prev ? { ...prev, aiTakeoverActive: data.aiTakeoverActive } : prev);
      await load();
    } catch {
      showToast(t('conversations.error_takeover'));
    }
  }

  const filters: Array<{ key: 'all' | 'OPEN' | 'RESOLVED'; label: string }> = [
    { key: 'all', label: t('conversations.filter_all') },
    { key: 'OPEN', label: t('conversations.filter_open') },
    { key: 'RESOLVED', label: t('conversations.filter_resolved') },
  ];

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 shadow-lg text-sm">
          {toast}
        </div>
      )}

      {/* Conversation list */}
      <div className="flex w-80 shrink-0 flex-col border-r">
        <div className="border-b px-4 py-3">
          <h1 className="font-semibold">{t('conversations.title')}</h1>
          <div className="mt-2 flex gap-1">
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`rounded px-2 py-0.5 text-xs font-medium ${
                  filter === f.key
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && (
            <p className="px-4 py-4 text-sm text-muted-foreground">{t('common.loading')}</p>
          )}
          {!loading && conversations.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <MessageSquare className="mb-2 h-8 w-8" />
              <p className="text-xs">{t('conversations.empty')}</p>
            </div>
          )}
          {conversations.map((conv) => (
            <button
              key={conv.id}
              onClick={() => selectConversation(conv)}
              className={`w-full border-b px-4 py-3 text-left hover:bg-muted/50 ${
                selected?.id === conv.id ? 'bg-muted' : ''
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-medium text-sm">
                  {conv.contactName ?? conv.contactPhone ?? 'Unknown'}
                </span>
                <span
                  className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                    CHANNEL_COLORS[conv.channel] ?? 'bg-muted text-muted-foreground'
                  }`}
                >
                  {conv.channel}
                </span>
              </div>
              <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                {conv.aiTakeoverActive && (
                  <span className="flex items-center gap-0.5 text-indigo-600">
                    <Bot className="h-3 w-3" />
                    {t('conversations.ai_takeover')}
                  </span>
                )}
                <span className="ml-auto">{sentimentIcon(conv.sentimentTrend)}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Message thread */}
      {selected ? (
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Conv header */}
          <div className="flex items-center justify-between border-b px-6 py-3">
            <div>
              <p className="font-semibold">
                {selected.contactName ?? selected.contactPhone ?? 'Unknown'}
              </p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span
                  className={`rounded-full px-1.5 py-0.5 font-medium ${
                    CHANNEL_COLORS[selected.channel] ?? 'bg-muted text-muted-foreground'
                  }`}
                >
                  {selected.channel}
                </span>
                {sparkline.length >= 2 && (
                  <span className="flex items-center gap-1">
                    {t('conversations.sentiment')}:
                    <SentimentSparklineSvg points={sparkline} />
                  </span>
                )}
              </div>
            </div>

            {/* AI takeover toggle */}
            <Button
              size="sm"
              variant={selected.aiTakeoverActive ? 'default' : 'outline'}
              onClick={toggleAiTakeover}
            >
              <Bot className="mr-1.5 h-3.5 w-3.5" />
              {selected.aiTakeoverActive
                ? t('conversations.disable_ai_takeover')
                : t('conversations.enable_ai_takeover')}
            </Button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
            {selected.messages.map((msg) => {
              const isOutbound = msg.direction === 'OUTBOUND';
              const isAi = msg.source === 'AI';
              return (
                <div
                  key={msg.id}
                  className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] rounded-2xl px-4 py-2.5 text-sm ${
                      isOutbound
                        ? isAi
                          ? 'bg-indigo-600 text-white'
                          : 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    }`}
                  >
                    {isAi && (
                      <div className="mb-1 flex items-center gap-1 text-xs opacity-75">
                        <Bot className="h-3 w-3" />
                        {t('conversations.ai_badge')}
                      </div>
                    )}
                    {!isOutbound && (
                      <div className="mb-1 flex items-center gap-1 text-xs opacity-60">
                        <User className="h-3 w-3" />
                      </div>
                    )}
                    <p className="leading-relaxed">{msg.body}</p>
                    <p
                      className={`mt-1 text-[10px] ${
                        isOutbound ? 'text-right opacity-60' : 'text-muted-foreground'
                      }`}
                    >
                      {formatTime(msg.createdAt)}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Draft suggestion */}
          {draft && (
            <div className="border-t bg-indigo-950/40 px-6 py-3">
              <p className="mb-1 text-xs font-medium text-indigo-300">
                {t('conversations.draft_label')}
              </p>
              <p className="mb-2 text-sm">{draft}</p>
              <div className="flex gap-2">
                <Button size="sm" onClick={useDraft}>
                  {t('conversations.use_draft')}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setDraft(null)}>
                  {t('conversations.discard_draft')}
                </Button>
              </div>
            </div>
          )}

          {/* Reply bar */}
          <div className="border-t px-6 py-3">
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleSuggest}
                disabled={suggesting}
                title={t('conversations.suggest_reply')}
              >
                <Sparkles className="h-3.5 w-3.5" />
                {suggesting ? t('conversations.suggesting') : t('conversations.suggest_reply')}
              </Button>
            </div>
            <div className="mt-2 flex gap-2">
              <textarea
                className="flex-1 resize-none rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                rows={2}
                placeholder={t('conversations.type_message')}
                value={messageBody}
                onChange={(e) => setMessageBody(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />
              <Button
                onClick={handleSend}
                disabled={sending || !messageBody.trim()}
                className="self-end"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center text-muted-foreground">
          <div className="text-center">
            <CheckCircle2 className="mx-auto mb-3 h-12 w-12" />
            <p className="text-sm">{t('conversations.empty')}</p>
          </div>
        </div>
      )}
    </div>
  );
}
